import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ManagedConfiguration } from '../../../hooks/api/useNswagClient';
import { PosIntegrationType, availableIntegrations } from '../enums/PosIntegrationType';
import { WasteType, wasteTypes } from '../enums/WasteType';
import {
  PosIntegrationParts,
  composePosIntegrationValue,
  emptyPosIntegrationParts,
  parsePosIntegrationValue,
  usesWasteType,
} from './posIntegrationValue';

interface PosIntegrationDialogProps {
  item?: ManagedConfiguration;
  saving: boolean;
  onApply: (value: string) => void;
  onClose: () => void;
}

// The server accepts RTS only with its own or the GL waste type, and Cloud likewise.
const ownWasteType = (integration: string) =>
  integration === PosIntegrationType.RTS ? WasteType.RTS : WasteType.Cloud;

const wasteTypesFor = (integration: string) =>
  wasteTypes.filter(option => option.key === WasteType.GL || option.key === ownWasteType(integration));

const usesConnectionId = (parts: PosIntegrationParts) =>
  parts.integration === PosIntegrationType.Simphony
  || parts.integration === PosIntegrationType.FRS
  || (usesWasteType(parts.integration) && parts.wasteType === WasteType.GL);

export const PosIntegrationDialog = ({ item, saving, onApply, onClose }: PosIntegrationDialogProps) => {
  const { t } = useTranslation('manageConfigurations');
  const [parts, setParts] = useState<PosIntegrationParts>(emptyPosIntegrationParts);

  useEffect(() => {
    setParts(item ? parsePosIntegrationValue(item.value) : emptyPosIntegrationParts);
  }, [item?.name, item?.value]);

  if (!item) return null;

  const update = (change: Partial<PosIntegrationParts>) => setParts(current => ({ ...current, ...change }));
  const composed = composePosIntegrationValue(parts);
  const changed = composed !== undefined && composed !== (item.value ?? '');

  // A stored value outside the standard lists stays visible and selectable so an existing value is
  // never silently replaced by editing an unrelated part of the setting.
  const hasUnknownIntegration = Boolean(parts.integration)
    && !availableIntegrations.some(option => option.key === parts.integration);
  const wasteOptions = wasteTypesFor(parts.integration);
  const hasUnknownWasteType = Boolean(parts.wasteType)
    && !wasteOptions.some(option => option.key === parts.wasteType);

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{t('pos.title')}</DialogTitle>
      <DialogContent>
        <Stack
          spacing={3}
          sx={{ mt: 1 }}
        >
          <Typography
            variant="textSM"
            color="text.secondary"
          >
            {t('pos.description')}
          </Typography>
          <TextField
            select
            label={t('integrationType')}
            value={parts.integration}
            onChange={event => update({ integration: event.target.value, wasteType: '', identifier: '' })}
            sx={{ mb: 0 }}
          >
            <MenuItem value="">{t('pos.notConfigured')}</MenuItem>
            {hasUnknownIntegration && (
              <MenuItem value={parts.integration}>{t('pos.unknownOption', { value: parts.integration })}</MenuItem>
            )}
            {availableIntegrations.map(option => (
              <MenuItem
                key={option.key}
                value={option.key}
              >
                {option.value}
              </MenuItem>
            ))}
          </TextField>
          {usesWasteType(parts.integration) && (
            <TextField
              select
              label={t('wasteType')}
              value={parts.wasteType}
              onChange={event => update({ wasteType: event.target.value, identifier: event.target.value === WasteType.GL ? parts.identifier : '' })}
              sx={{ mb: 0 }}
            >
              {hasUnknownWasteType && (
                <MenuItem value={parts.wasteType}>{t('pos.unknownOption', { value: parts.wasteType })}</MenuItem>
              )}
              {wasteOptions.map(option => (
                <MenuItem
                  key={option.key}
                  value={option.key}
                >
                  {option.value}
                </MenuItem>
              ))}
            </TextField>
          )}
          {usesConnectionId(parts) && (
            <TextField
              label={t('posConnection')}
              value={parts.identifier}
              onChange={event => update({ identifier: event.target.value })}
              sx={{ mb: 0 }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="secondary"
          size="md"
          onClick={onClose}
        >
          {t('pos.cancel')}
        </Button>
        <Button
          variant="primary"
          size="md"
          disabled={saving || !changed}
          onClick={() => changed && onApply(composed)}
        >
          {t('pos.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
